import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Grid, IconButton, MenuItem, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import moment from "moment";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import attendanceServices from "app/services/attendance-services";
import {downloadBuffer} from "app/utils/appHelpers";
import ExcelExportButton from "app/widgets/Shared/ExcelExportButton";
import MarkDialog from "./MarkDialog";
import ScheduleDialog from "./ScheduleDialog";
import {DIAS, DIAS_CORTOS, ESTADOS, diferencia, duracion, fechaCorta, hhmm} from "./attendanceFormat";

// Asistencia del equipo de Kargotools que trabaja a distancia: cada uno marca su entrada y
// salida (la hora la pone el servidor) y se compara con su horario semanal. Solo superadmin
// con correo @kargotools.com, el mismo criterio que el backend (AttendanceController).
const GRIS = "#6B7280";
const encabezado = {
    textTransform: "uppercase", letterSpacing: 0.6, fontSize: 13, fontWeight: 600, color: GRIS,
    borderBottomColor: "#E5E7EB", whiteSpace: "nowrap"
};

const Estado = ({status}) => {
    const e = ESTADOS[status] || ESTADOS.pendiente;
    return (
        <Box component="span" sx={{
            display: "inline-block", px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: e.bg, color: e.color,
            fontSize: 13, fontWeight: 600, whiteSpace: "nowrap"
        }}>{e.label}</Box>
    );
};

const Kpi = ({label, value, hint, color}) => (
    <Card variant="outlined" sx={{height: "100%"}}>
        <CardContent>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h3" sx={{my: 0.5, color}}>{value}</Typography>
            {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        </CardContent>
    </Card>
);

// El reloj del tramo abierto, que avanza solo.
const useAhora = (activo) => {
    const [ahora, setAhora] = useState(Date.now());
    useEffect(() => {
        if (!activo) return undefined;
        const id = setInterval(() => setAhora(Date.now()), 30000);
        return () => clearInterval(id);
    }, [activo]);
    return ahora;
};

const AttendanceIndex = () => {
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const {authUser} = useJumboAuth();
    const [userId, setUserId] = useState("");
    const [desde, setDesde] = useState(moment().startOf("month").format("YYYY-MM-DD"));
    const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [marking, setMarking] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [note, setNote] = useState("");
    const [markDialog, setMarkDialog] = useState(null); // {date, mark}
    const [scheduleOpen, setScheduleOpen] = useState(false);

    const allowed = useMemo(() => {
        const superadmin = (authUser?.profiles || []).some((p) => p.name === "superadmin");
        return superadmin && /@kargotools\.com$/i.test(authUser?.email || "");
    }, [authUser]);

    useEffect(() => {
        if (authUser && !allowed) navigate("/trips");
    }, [authUser, allowed, navigate]);

    const params = useMemo(() => ({user_id: userId, desde, hasta}), [userId, desde, hasta]);

    const cargar = useCallback(async () => {
        try {
            setLoading(true);
            setData(await attendanceServices.report(params));
        } catch (error) {
            if (error?.response?.status === 403) navigate("/trips");
        } finally {
            setLoading(false);
        }
    }, [params, navigate]);

    useEffect(() => {
        if (allowed) cargar();
    }, [allowed, cargar]);

    const propia = data && data.user.id === data.me;
    const abierto = propia ? data.open_mark : null;
    const ahora = useAhora(Boolean(abierto));
    const hoy = data?.days.find((d) => d.date === data.today);

    const marcar = async () => {
        try {
            setMarking(true);
            if (abierto) {
                await attendanceServices.checkOut(note);
                enqueueSnackbar("Salida marcada", {variant: "success"});
            } else {
                await attendanceServices.checkIn(note);
                enqueueSnackbar("Entrada marcada", {variant: "success"});
            }
            setNote("");
            await cargar();
        } catch (_error) {
            // El interceptor global muestra el motivo.
        } finally {
            setMarking(false);
        }
    };

    const exportar = async () => {
        try {
            setExporting(true);
            const buffer = await attendanceServices.downloadXlsx(params);
            downloadBuffer(buffer, `asistencia_${data.user.email.split("@")[0]}_${desde}_${hasta}.xlsx`);
        } finally {
            setExporting(false);
        }
    };

    if (!allowed) return null;

    const t = data?.totals;
    const diaDeHoy = DIAS[moment().isoWeekday()];

    return (
        <Stack spacing={2}>
            <Stack direction={{xs: "column", md: "row"}} spacing={2} justifyContent="space-between"
                   alignItems={{md: "flex-end"}}>
                <div>
                    <Typography variant="h2" sx={{mb: 0.5}}>Asistencia</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Entradas y salidas del equipo que trabaja a distancia, contra su horario. La hora la pone el
                        servidor al marcar; lo que se carga o corrige a mano queda señalado.
                    </Typography>
                </div>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {data && (
                        <TextField select size="small" label="Persona" value={userId || data.user.id}
                                   onChange={(e) => setUserId(e.target.value)} sx={{minWidth: 200}}>
                            {data.team.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                        </TextField>
                    )}
                    <TextField size="small" type="date" label="Desde" value={desde}
                               onChange={(e) => setDesde(e.target.value)} InputLabelProps={{shrink: true}}/>
                    <TextField size="small" type="date" label="Hasta" value={hasta}
                               onChange={(e) => setHasta(e.target.value)} InputLabelProps={{shrink: true}}/>
                    <Button variant="outlined" startIcon={<EventNoteOutlinedIcon/>} disabled={!data}
                            onClick={() => setScheduleOpen(true)}>
                        Horario
                    </Button>
                    <ExcelExportButton onClick={exportar} loading={exporting} disabled={!data}/>
                </Stack>
            </Stack>

            {!data ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress/></Box>
            ) : (
                <>
                    {propia && (
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction={{xs: "column", md: "row"}} spacing={3}
                                       alignItems={{md: "center"}} justifyContent="space-between">
                                    <div>
                                        <Typography variant="overline" color="text.secondary">
                                            Hoy, {diaDeHoy.toLowerCase()} {fechaCorta(data.today)}
                                        </Typography>
                                        <Typography variant="h3" sx={{mb: 0.5}}>
                                            {abierto
                                                ? `Trabajando desde las ${hhmm(abierto.check_in_at)} · ${duracion((ahora - Date.parse(abierto.check_in_at)) / 1000)}`
                                                : hoy?.marks.length ? `Llevas ${duracion(hoy.worked_s)} hoy` : "Todavía no marcas entrada"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Horario de hoy: {hoy?.planned.length
                                                ? hoy.planned.map((b) => `${b.start}–${b.end}`).join(", ")
                                                : "sin horario"}
                                            {hoy?.marks.length ? ` · Tramos: ${hoy.marks.map((m) => `${hhmm(m.check_in_at)}–${hhmm(m.check_out_at) || "…"}`).join(", ")}` : ""}
                                        </Typography>
                                    </div>
                                    <Stack direction={{xs: "column", sm: "row"}} spacing={1} alignItems={{sm: "center"}}>
                                        <TextField size="small" value={note} onChange={(e) => setNote(e.target.value)}
                                                   placeholder={abierto ? "¿En qué trabajaste? (opcional)" : "Nota (opcional)"}
                                                   sx={{minWidth: {sm: 280}}}/>
                                        <LoadingButton variant="contained" size="large" loading={marking}
                                                       color={abierto ? "error" : "primary"}
                                                       startIcon={abierto ? <LogoutIcon/> : <LoginIcon/>}
                                                       onClick={marcar} sx={{whiteSpace: "nowrap"}}>
                                            {abierto ? "Marcar salida" : "Marcar entrada"}
                                        </LoadingButton>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                            <Kpi label="Trabajado" value={duracion(t.worked_s)} hint={`de ${duracion(t.planned_s)} planificadas`}/>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Kpi label="Diferencia" value={diferencia(t.diff_s)}
                                 color={t.diff_s < -300 ? "#B45309" : undefined}/>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Kpi label="Días con atraso" value={t.late_days} hint="Más de 5 min tarde a la primera entrada"/>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Kpi label="Ausencias" value={t.absent_days}
                                 hint={t.unclosed_days ? `${t.unclosed_days} día(s) sin salida` : "Días con horario sin marcas"}
                                 color={t.absent_days ? "#B91C1C" : undefined}/>
                        </Grid>
                    </Grid>

                    <Card variant="outlined">
                        <TableContainer sx={{opacity: loading ? 0.5 : 1}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={encabezado}>Día</TableCell>
                                        <TableCell sx={encabezado}>Horario</TableCell>
                                        <TableCell sx={encabezado}>Tramos marcados</TableCell>
                                        <TableCell sx={encabezado} align="right">Trabajado</TableCell>
                                        <TableCell sx={encabezado} align="right">Diferencia</TableCell>
                                        <TableCell sx={encabezado}>Estado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.days.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{py: 4, textAlign: "center", color: GRIS}}>
                                                Sin días con horario ni marcas en estas fechas.
                                                {!data.schedule.valid_from && " Configura el horario con el botón Horario."}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {data.days.map((d) => (
                                        <TableRow key={d.date} hover>
                                            <TableCell sx={{whiteSpace: "nowrap"}}>
                                                <b>{DIAS_CORTOS[d.weekday]}</b> {fechaCorta(d.date)}
                                            </TableCell>
                                            <TableCell sx={{color: GRIS, whiteSpace: "nowrap"}}>
                                                {d.planned.length ? d.planned.map((b) => `${b.start}–${b.end}`).join(", ") : "—"}
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                                                    {d.marks.map((m) => (
                                                        <Tooltip key={m.id} title={[
                                                            m.note,
                                                            m.edited_by && `Corregido por ${m.edited_by} el ${m.edited_at.slice(0, 10)} ${hhmm(m.edited_at)}`
                                                        ].filter(Boolean).join(" · ") || "Editar"}>
                                                            <Chip size="small" variant={m.edited_by ? "outlined" : "filled"}
                                                                  label={`${hhmm(m.check_in_at)}–${hhmm(m.check_out_at) || "…"}${m.edited_by ? " ✎" : ""}`}
                                                                  onClick={() => setMarkDialog({date: d.date, mark: m})}
                                                                  onDelete={() => setMarkDialog({date: d.date, mark: m})}
                                                                  deleteIcon={<EditOutlinedIcon/>}/>
                                                        </Tooltip>
                                                    ))}
                                                    {d.late_s > 0 && (
                                                        <Typography variant="caption" sx={{color: "#B45309"}}>
                                                            {duracion(d.late_s)} tarde
                                                        </Typography>
                                                    )}
                                                    <Tooltip title="Agregar tramo">
                                                        <IconButton size="small" onClick={() => setMarkDialog({date: d.date, mark: null})}>
                                                            <AddIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right" sx={{whiteSpace: "nowrap"}}>{duracion(d.worked_s)}</TableCell>
                                            <TableCell align="right" sx={{
                                                whiteSpace: "nowrap", color: d.diff_s < -300 ? "#B45309" : GRIS
                                            }}>{d.planned_s ? diferencia(d.diff_s) : "—"}</TableCell>
                                            <TableCell><Estado status={d.status}/></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                    <Button startIcon={<AddIcon/>} sx={{alignSelf: "flex-start"}}
                            onClick={() => setMarkDialog({date: data.today, mark: null})}>
                        Agregar un tramo olvidado
                    </Button>
                </>
            )}

            <MarkDialog open={Boolean(markDialog)} onClose={() => setMarkDialog(null)}
                        onSaved={() => { setMarkDialog(null); cargar(); }}
                        userId={data?.user.id} date={markDialog?.date} mark={markDialog?.mark}/>
            <ScheduleDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)}
                            onSaved={() => { setScheduleOpen(false); cargar(); }}
                            userId={data?.user.id} schedule={data?.schedule}/>
        </Stack>
    );
};

export default AttendanceIndex;
